import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ContentChild,
  Renderer2,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  OnInit,
  DestroyRef,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { LetDirective } from '@directives/app-let.directive';
import { InputDirective } from '@directives/input/input.directive';
import { NullableString } from '@models/app.models';
import { Store } from '@ngrx/store';
import { RequiredFieldPipe } from '@pipes/required-field/required-field.pipe';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import { stringAsBooleanProp } from '@utils/data-utils';
import { InputErrorsComponent } from '../input-errors/input-errors.component';
import {
  SafeHtmlPipe,
  SanitizerValueType,
} from '@pipes/safe-html/safe-html.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EmailTipDirective } from '@directives/email-tip/email-tip.directive';

/**
 * The main component for regular HTML inputs, i.e. text,
 * number, etc. Takes the input as a child, which must be tagged
 * with the appInput directive.
 */
@Component({
  selector: 'app-input',
  standalone: true,
  imports: [
    CommonModule,
    RequiredFieldPipe,
    FormsModule,
    ReactiveFormsModule,
    InputDirective,
    InputErrorsComponent,
    LetDirective,
    SafeHtmlPipe,
    EmailTipDirective,
  ],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [RequiredFieldPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements OnInit, OnChanges, AfterViewInit {
  showEmailTip: boolean;

  constructor(
    private requiredPipe: RequiredFieldPipe,
    private renderer: Renderer2,
    private store: Store,
    private changeDetector: ChangeDetectorRef,
  ) {}

  @ViewChild('container') container: ElementRef | null = null;
  @ContentChild(InputDirective) input: InputDirective | null = null;

  @Input() label?: string;
  @Input() group?: string | number;
  // Setting the value allows us to react by setting
  // data-value on selects, which is required to get the
  // correct behavior from the bootstrap floating label.
  @Input() value?: string;
  @Input() extraContainerClasses: string;
  @Input() control?: AbstractControl | null;
  @Input() extraErrors?: { [key: string]: string };
  @Input() suppressErrors?: string[];
  @Input() dynamicRequired: boolean;
  @Input() addressAutocomplete = false;
  @Input() addressType: ('establishment' | 'street_address')[] = ['street_address'];
  @Input() withEmailTip = false;
  @Input() customInput = false;
  @Input() hideErrorLabel = false;
  @Output() placeSelected = new EventEmitter<google.maps.places.PlaceResult>();
  appCopy$ = this.store.select(selectAppCmsCopy);

  autocomplete: google.maps.places.Autocomplete;

  readonly SanitizerValueType = SanitizerValueType;

  get inputsLoaded(): boolean {
    return !!this.input?.nativeElement && !!this.container?.nativeElement;
  }

  public _noLabel = false;
  @Input()
  get noLabel(): string | boolean {
    return this._noLabel;
  }

  set noLabel(value: string | boolean) {
    this._noLabel =
      typeof value === 'string' ? stringAsBooleanProp(value) : value;
  }

  id: string;
  required: boolean;
  type: string | null;
  inputValue: string = '';

  private destroyRef = inject(DestroyRef);


  ngOnInit(): void {
    this.setInputValueByControlValueChanges();
  }

  ngAfterViewInit(): void {
    // the input should always be set, but maybe someone
    // forgot the appInput directive here.
    if (!this.inputsLoaded) {
      console.warn(
        'An input decorated with the appInput directive must be supplied to the InputComponent (1)',
      );
      return;
    }

    // we know these are not null because of the inputsLoaded check above
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const el = this.input!.nativeElement;

    this.type =
      (el instanceof HTMLInputElement
        ? el.getAttribute('type')
        : el.tagName
      )?.toLowerCase() ?? null;

    if (this.addressAutocomplete && el instanceof HTMLInputElement) {
      this.autocomplete = new google.maps.places.Autocomplete(el, {
        componentRestrictions: { country: 'US' },
        fields: ['address_components', 'formatted_address'],
        types: this.addressType,
      });
      /* istanbul ignore next */
      google.maps.event.addListener(this.autocomplete, 'place_changed', () => {
        const place = this.autocomplete.getPlace();
        this.placeSelected.emit(place);
      });
    }

    this.renderer.addClass(
      this.container!.nativeElement,
      this.extraContainerClasses,
    );

    /* eslint-enable @typescript-eslint/no-non-null-assertion */

    switch (this.type) {
      case 'radio':
        this.renderer.addClass(el, 'btn-check');
        break;
      case 'checkbox':
        this.renderer.addClass(el, 'form-check-input');
        break;
      case 'submit':
      case 'button':
        this.renderer.addClass(el, 'btn');
        this.renderer.addClass(el, 'btn-primary');
        break;
      case 'select':
        this.renderer.addClass(el, 'form-select');
        this.renderer.addClass(el, 'custom-select');
        break;
      case 'textarea':
      default:
        this.renderer.addClass(el, 'form-control');
    }

    this.setInputProperties();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setInputProperties(changes);
    this.autocomplete?.setTypes(this.addressType);
  }

  setInputProperties(changes?: SimpleChanges): void {
    // the first changes may run before the view is initialized
    if (!this.inputsLoaded) {
      return;
    }

    // we know these are not null because of the inputsLoaded check above
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const el = this.input!.nativeElement;
    /* eslint-enable @typescript-eslint/no-non-null-assertion */

    const { id, name } = el;
    // If dynamicRequired attribute passed then use that value else rely on static required attribute set on the HTML element.
    // If required attribute dynamically changed from calling places then since <input> element is a child of <app-input> control it won't rerender <app-input>
    // So passing it directly from <app-input> control will solve this issue.
    const required =
      this.dynamicRequired === undefined
        ? !(el instanceof HTMLButtonElement)
          ? el.required
          : false
        : this.dynamicRequired;

    const placeholder =
      el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
        ? el.placeholder
        : null;

    const hasNoValidIdentifier = !id && !name;
    if (hasNoValidIdentifier) {
      console.error(
        'Inputs should have either name or id or both, this input has neither.',
      );
    }

    // set values from inner input to align view
    // with child.
    this.required = required;
    this.id = id;

    // set name to id if there is an id set but no name.
    // this.id is set above from name and group if it was not set
    // on the element.
    if (id && !name) {
      this.renderer.setProperty(el, 'name', id);
    }

    // The data-qa attributes are used by the E2E tests.
    this.renderer.setAttribute(el, 'data-qa', name + '-input');

    // no changes or group changed, so we update the id from the
    // name and group.
    if ((!changes || changes.group) && !id && name) {
      const group =
        (changes?.group?.currentValue as string | number | undefined) ??
        this.group;
      this.id = group !== undefined ? `${name}-${group}` : name;
      this.renderer.setProperty(el, 'id', this.id);
    }

    // no changes or label changed, so we update the placeholder
    // based on the label.
    if ((!changes || changes.label) && !placeholder) {
      const label =
        (changes?.label?.currentValue as NullableString) ?? this.label;
      if (label) {
        this.renderer.setProperty(
          el,
          'placeholder',
          required ? this.requiredPipe.transform(label) : label,
        );
        this.renderer.setAttribute(el, 'aria-label', label);
      }
    }

    // updating data-value allows the bootstrap floating
    // label behavior to work automatically for selects
    if (changes?.value || this.value) {
      const value =
        (changes?.value?.currentValue as NullableString) ?? this.value;
      if (value) {
        this.renderer.setAttribute(el, 'data-value', value);
      }
    }

    el.addEventListener('focus', () => {
      this.showEmailTip = true;
      this.changeDetector.detectChanges();
    });

    el.addEventListener('focusout', () => {
      setTimeout(() => {
        this.showEmailTip = false;
        this.changeDetector.detectChanges();
      }, 100);
    });

    this.changeDetector.detectChanges();
  }

  copyEmail(email: string): void {
    this.control?.setValue(email);
    this.changeDetector.detectChanges();
  }

  private setInputValueByControlValueChanges(): void {
    this.control?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        this.inputValue = value;
        this.showEmailTip = !!value;
        this.changeDetector.detectChanges();
      });
  }
}
